// Pure rebalancing logic — no DOM access, so it unit-tests in plain Node the
// same way core.ts helpers do. rebalance-ui.ts owns everything visual.

export type TargetClass = 'gold' | 'stocks' | 'bonds' | 'cash';

export const TARGET_CLASSES: readonly TargetClass[] = ['gold', 'stocks', 'bonds', 'cash'];

export const TARGET_CLASS_LABELS: Record<TargetClass, string> = {
  gold: 'Gold',
  stocks: 'Stocks',
  bonds: 'Bonds',
  cash: 'Cash',
};

export type ClassValues = Record<TargetClass, number>;

// Amounts smaller than this (in the reporting currency) are treated as noise so
// floating-point residue never shows up as a spurious "buy £0.00".
const EPSILON = 0.005;

/**
 * Map a free-form asset-class label (from the CSV or a known override) onto one
 * of the four core classes. Returns null for anything we can't confidently
 * bucket so the caller can surface it as unclassified rather than mis-file it.
 */
export function classifyAssetClass(label: string | null | undefined): TargetClass | null {
  const text = String(label ?? '').trim().toLowerCase();
  if (!text) return null;
  // Anchor each keyword to a word start (\b) so it still matches plurals and
  // suffixes ("equities", "bonds", "liquidity") but never fires inside an
  // unrelated word — e.g. "liquid" must not match "Illiquid Alternatives".
  if (/\b(gold|precious|bullion)/.test(text)) return 'gold';
  if (/\b(cash|money market|liquid|deposit)/.test(text)) return 'cash';
  if (/\b(bond|gilt|treasur|fixed[\s-]?income|gov(ernment)? debt)/.test(text)) return 'bonds';
  if (/\b(stock|equit|share)/.test(text)) return 'stocks';
  return null;
}

// Physical-gold instruments the app knows about. A gold holding is often filed
// under a broad label like "Commodities" (see docs/csv-format.md), which the
// label classifier can't safely map to gold — commodities also covers things
// like uranium — so we recognise the instrument itself by ISIN, ticker or
// resolved symbol.
const GOLD_INSTRUMENTS = new Set([
  'SGLP', 'SGLN', 'SGLP.L', 'SGLN.L',
  'IE00B579F325', 'IE00B4ND3602',
]);

/**
 * Classify a holding into a core class, trying its asset-class label first and
 * then falling back to recognising a known gold instrument by any of its
 * identifiers. Returns null when nothing matches so the caller can surface it as
 * unclassified.
 */
export function classifyHolding(
  label: string | null | undefined,
  identifiers: ReadonlyArray<string | null | undefined>,
): TargetClass | null {
  const byLabel = classifyAssetClass(label);
  if (byLabel) return byLabel;
  for (const identifier of identifiers) {
    const key = String(identifier ?? '').trim().toUpperCase();
    if (key && GOLD_INSTRUMENTS.has(key)) return 'gold';
  }
  return null;
}

export function emptyValues(): ClassValues {
  return { gold: 0, stocks: 0, bonds: 0, cash: 0 };
}

export function sumValues(values: ClassValues): number {
  return TARGET_CLASSES.reduce((total, cls) => total + (values[cls] || 0), 0);
}

export function targetsSumToHundred(targets: ClassValues): boolean {
  return Math.abs(sumValues(targets) - 100) < 0.01;
}

export type Action = 'buy' | 'sell' | 'hold';

export interface RebalanceRow {
  assetClass: TargetClass;
  currentValue: number;
  currentPct: number;
  targetPct: number;
  targetValue: number;
  delta: number; // targetValue - currentValue (positive = buy, negative = sell)
  action: Action;
  amount: number; // absolute size of the trade
}

export interface RebalancePlan {
  total: number;
  rows: RebalanceRow[];
  totalBuy: number;
  totalSell: number;
}

/**
 * Rebalance the existing portfolio: keep the total invested value fixed and work
 * out, per class, how much to sell (overweight) or buy (underweight) so the mix
 * matches the targets. Sells fund the buys, so totalBuy ≈ totalSell.
 */
export function computeRebalance(current: ClassValues, targetPct: ClassValues): RebalancePlan {
  const total = sumValues(current);
  const rows = TARGET_CLASSES.map((assetClass): RebalanceRow => {
    const currentValue = current[assetClass] || 0;
    const targetValue = (targetPct[assetClass] / 100) * total;
    const delta = targetValue - currentValue;
    const action: Action = Math.abs(delta) < EPSILON ? 'hold' : delta > 0 ? 'buy' : 'sell';
    return {
      assetClass,
      currentValue,
      currentPct: total > 0 ? (currentValue / total) * 100 : 0,
      targetPct: targetPct[assetClass],
      targetValue,
      delta,
      action,
      amount: Math.abs(delta),
    };
  });

  const totalBuy = rows.reduce((sum, row) => (row.action === 'buy' ? sum + row.amount : sum), 0);
  const totalSell = rows.reduce((sum, row) => (row.action === 'sell' ? sum + row.amount : sum), 0);
  return { total, rows, totalBuy, totalSell };
}

export interface CashToTargetPlan {
  currentTotal: number;
  reachable: boolean; // can the target be reached by buying only (no selling)?
  minCash: number; // extra cash needed to reach the target without selling
  newTotal: number; // currentTotal + minCash
  blockingClasses: TargetClass[]; // held classes with a 0% target — they force a sale
}

/**
 * How much fresh cash would it take to reach the target purely by buying, with
 * no selling? Buying only ever grows the total, so the binding constraint is the
 * most-overweight class: the total must grow until that class's current value is
 * no more than its target share. A class you hold but target at 0% can never be
 * diluted away by buying, so it makes the target unreachable without a sale.
 */
export function computeCashToTarget(current: ClassValues, targetPct: ClassValues): CashToTargetPlan {
  const currentTotal = sumValues(current);
  let requiredTotal = currentTotal;
  const blockingClasses: TargetClass[] = [];

  for (const cls of TARGET_CLASSES) {
    const value = current[cls] || 0;
    if (value <= EPSILON) continue;
    const fraction = targetPct[cls] / 100;
    if (fraction <= 0) {
      blockingClasses.push(cls);
      continue;
    }
    requiredTotal = Math.max(requiredTotal, value / fraction);
  }

  const reachable = blockingClasses.length === 0;
  return {
    currentTotal,
    reachable,
    minCash: reachable ? Math.max(0, requiredTotal - currentTotal) : 0,
    newTotal: reachable ? requiredTotal : currentTotal,
    blockingClasses,
  };
}

export interface ContributionRow {
  assetClass: TargetClass;
  currentValue: number;
  currentPct: number; // share of the post-contribution total
  targetPct: number;
  targetValue: number;
  buy: number; // new cash deployed into this class
  projectedValue: number;
  projectedPct: number; // share of the post-contribution total after buying
  overweight: boolean; // already above target — can't be fixed by buying alone
  sellToReachTarget: number; // amount to sell if the user also rebalances (0 unless overweight)
}

export interface ContributionPlan {
  cash: number;
  currentTotal: number;
  newTotal: number;
  rows: ContributionRow[];
  deployed: number; // cash actually put to work
  leftover: number; // cash left over once every class is at target (only when nothing is overweight)
  needsRebalance: boolean; // some class overweight — pure buying can't hit the target
  sellNeeded: number; // total to sell across overweight classes to fully reach target
}

/**
 * Invest fresh cash without selling: spread the new money across the classes
 * that are below their target so the overall mix moves toward it. If a class is
 * already above its target (even after the new cash dilutes it), buying alone
 * can't get there — we flag needsRebalance and report how much to sell.
 */
export function computeContribution(
  current: ClassValues,
  targetPct: ClassValues,
  cash: number,
): ContributionPlan {
  const currentTotal = sumValues(current);
  const contribution = Math.max(0, cash);
  const newTotal = currentTotal + contribution;

  const needs = TARGET_CLASSES.map((assetClass) => {
    const currentValue = current[assetClass] || 0;
    const targetValue = (targetPct[assetClass] / 100) * newTotal;
    return { assetClass, currentValue, targetValue, need: targetValue - currentValue };
  });

  // Positive needs are the classes we can top up. Their sum is at least `cash`
  // (it exceeds it exactly when some class is overweight), so a proportional
  // split never over-allocates a class beyond its target.
  const positiveNeed = needs.reduce((sum, item) => (item.need > 0 ? sum + item.need : sum), 0);

  const rows = needs.map(({ assetClass, currentValue, targetValue, need }): ContributionRow => {
    const buy = need > EPSILON && positiveNeed > 0
      ? contribution * (need / positiveNeed)
      : 0;
    const projectedValue = currentValue + buy;
    const overweight = need < -EPSILON;
    return {
      assetClass,
      currentValue,
      currentPct: newTotal > 0 ? (currentValue / newTotal) * 100 : 0,
      targetPct: targetPct[assetClass],
      targetValue,
      buy,
      projectedValue,
      projectedPct: newTotal > 0 ? (projectedValue / newTotal) * 100 : 0,
      overweight,
      sellToReachTarget: overweight ? currentValue - targetValue : 0,
    };
  });

  const deployed = rows.reduce((sum, row) => sum + row.buy, 0);
  const sellNeeded = rows.reduce((sum, row) => sum + row.sellToReachTarget, 0);
  return {
    cash: contribution,
    currentTotal,
    newTotal,
    rows,
    deployed,
    leftover: Math.max(0, contribution - deployed),
    needsRebalance: sellNeeded > EPSILON,
    sellNeeded,
  };
}
